import "./bootstrap";
import "@/assets/css/app.scss";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import ErrorBoundary from "@/ErrorBoundary";
import Login from "@/Pages/Auth/Login";
import PrivateRoute from "@/Routes/PrivateRoute";
import PublicRoute from "@/Routes/PublicRoute";

import { routingConstants } from "./Constants/routingConstants";
import MainLogin from "./Layouts/MainLogin";
import { _localSet } from "./localStorage";
import OtpVerification from "./Pages/Auth/OtpVerification";
import CandidateCreate from "./Pages/Candidate/CandidateCreate";
import EditCandidate from "./Pages/Candidate/EditCandidate";

//* CANDIDATE LISTING
import Applied from "./Pages/CandidatesListing/Applied";
import Booked from "./Pages/CandidatesListing/Booked";
import Invited from "./Pages/CandidatesListing/Invited";
import Worked from "./Pages/CandidatesListing/Worked";
import LatestAnnouncement from "./Pages/Client/Announcement/LatestAnnouncement";
import CandidateDetail from "./Pages/Client/CandidateDetail";
import ContractDetail from "./Pages/Client/ContractDetail";
import Unfilled from "./Pages/Client/Contracts/Unfilled";
import CreateJob from "./Pages/Client/CreateJob";
import Dashboard from "./Pages/Client/Dashboard";
import Downloads from "./Pages/Client/Downloads";
import Invoice from "./Pages/Client/Invoice";
import Profile from "./Pages/Client/Profile";
import AddRoleAccess from "./Pages/Client/UserAccess/RoleAccess/AddRoleAccess";
import EditRoleAccess from "./Pages/Client/UserAccess/RoleAccess/EditRoleAccess";
import RoleAccess from "./Pages/Client/UserAccess/RoleAccess/RoleAccess";
import Users from "./Pages/Client/UserAccess/Users";
import DashboardRedirect from "./Pages/DashboardRedirect";
import Logout from "./Pages/Logout";
import { RedirectNotFoundPage } from "./Pages/RedirectNotFoundPage";
//* TIMESHEETS
import Assigned from "./Pages/Timesheets/Assigned";
import Authorised from "./Pages/Timesheets/Authorised";
import Submitted from "./Pages/Timesheets/Submitted";
import Terminated from "./Pages/Timesheets/Terminated";
import PremiumRoute from "./Routes/PremiumRoute";
import RestrictedRoute from "./Routes/RestrictedRoute";

const _routes = [
  {
    path: routingConstants.dashboardRedirect,
    element: <DashboardRedirect />,
  },
  {
    path: routingConstants.candidateCreate,
    element: <CandidateCreate />,
  },
  {
    path: routingConstants.candidateEdit,
    element: <EditCandidate />,
  },
  {
    path: routingConstants.contractDetailRoute,
    element: <ContractDetail />,
  },
  {
    path: routingConstants.invitedCandidate,
    element: <Invited />,
    isRestricted: true,
    accessKey: "is_allow_to_invited_list",
  },
  {
    path: routingConstants.appliedCandidate,
    element: <Applied />,
    isRestricted: true,
    accessKey: "is_allow_to_applied_list",
    isPremium: true,
    specificAccess: "client_allow_publish_job", // id client basic service -> grant access to applied
  },
  {
    path: routingConstants.bookedCandidate,
    element: <Booked />,
    isRestricted: true,
    accessKey: "is_allow_to_booked_list",
  },
  {
    path: routingConstants.workedCandidate,
    element: <Worked />,
    isRestricted: true,
    accessKey: "is_allow_to_worked_list",
  },
  {
    path: routingConstants.unfilledContracts,
    element: <Unfilled />,
    isPremium: true,
    isRestricted: true,
    accessKey: "is_allow_to_unfilled_list",
    specificAccess: "client_allow_publish_job", // id client basic service -> grant access to unfilled section
  },
  {
    path: routingConstants.candidateDetailRoute,
    element: <CandidateDetail />,
  },
  {
    path: routingConstants.createJob,
    element: <CreateJob />,
    isPremium: true,
    isRestricted: true,
    accessKey: "is_allow_to_publish_job",
    specificAccess: "client_allow_publish_job", // id client basic service -> grant access to publish job
  },
  {
    path: routingConstants.invoice,
    element: <Invoice />,
    isRestricted: true,
    accessKey: "is_allow_to_invoice_list",
  },
  {
    path: routingConstants.assignedTimesheet,
    element: <Assigned />,
    isRestricted: true,
    accessKey: "is_allow_to_assigned_timesheet_list",
  },
  {
    path: routingConstants.submittedTimesheet,
    element: <Submitted />,
    isRestricted: true,
    accessKey: "is_allow_to_submitted_timesheet_list",
  },
  {
    path: routingConstants.authorisedTimesheet,
    element: <Authorised />,
    isRestricted: true,
    accessKey: "is_allow_to_authorised_timesheet_list",
  },
  {
    path: routingConstants.terminatedTimesheet,
    element: <Terminated />,
    isPremium: true,
    isRestricted: true,
    accessKey: "is_allow_to_terminated_timesheet_list",
  },
  {
    path: routingConstants.downloads,
    element: <Downloads />,
  },
  {
    path: routingConstants.profile,
    element: <Profile />,
  },
  {
    path: routingConstants.clientRole,
    element: <RoleAccess />,
    isRestricted: true,
    accessKey: "is_allow_to_users_and_roles_list",
  },
  {
    path: routingConstants.clientAddRole,
    element: <AddRoleAccess />,
  },
  {
    path: routingConstants.clientEditRoleRoute,
    element: <EditRoleAccess />,
  },
  {
    path: routingConstants.clientAnnouncement,
    element: <LatestAnnouncement />,
  },
  {
    path: routingConstants.clientUsers,
    element: <Users />,
  },
];

function App() {
  const emailRedirectionLocalStorageMapping = [
    { key: "flag", condition: "&flag=1", redirect: routingConstants.invoice },
    { key: "tmsauth", condition: "tmsauth" },
    { key: "vcomp", condition: "vcomp" },
    { key: "vcont", condition: "vcont" },
    { key: "vapplied", condition: "vapplied" },
    { key: "userauth", condition: "userauth" },
    { key: "appchat", condition: "appchat" },
  ];

  emailRedirectionLocalStorageMapping?.forEach(
    ({ key, condition, redirect }) => {
      if (window.location.pathname?.includes(condition)) {
        _localSet(key, window.location.pathname);
        if (redirect) window.location.href = redirect;
      }
    },
  );

  const checkRestricted = (route) => {
    if (route.isRestricted) {
      return (
        <RestrictedRoute accessKey={route.accessKey}>
          {route.element}
        </RestrictedRoute>
      );
    }
    return route.element;
  };

  const checkPremium = (route) => {
    return route.isPremium ? (
      <PremiumRoute specificAccess={route.specificAccess}>
        {checkRestricted(route)}
      </PremiumRoute>
    ) : (
      checkRestricted(route)
    );
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={
            <PublicRoute>
              <MainLogin />
            </PublicRoute>
          }
        >
          <Route path={`/${routingConstants.login}`} element={<Login />} />
          <Route
            path={`/${routingConstants.otpVerificationRoute}`}
            element={<OtpVerification />}
          />
        </Route>
        <Route path="/" element={<PrivateRoute />}>
          <Route index element={<Dashboard />} />
          {_routes.map((route, index) => {
            return (
              <Route
                key={index}
                path={route.path}
                element={checkPremium(route)}
              />
            );
          })}
        </Route>

        <Route path="/logout" element={<Logout />} />
        <Route
          path="*"
          element={
            <ErrorBoundary>
              <RedirectNotFoundPage />
            </ErrorBoundary>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
